// Similar Stays rail — sits at the bottom of every stay detail page.
// Matches by overlap of `experience_types` (food_forward, riverside, etc).
//
// • Mobile  → horizontal swipe carousel
// • Desktop → 4-card grid
// • Title   → "Stays you might also love"
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useWishlist } from '../hooks/useWishlist';

const API = process.env.REACT_APP_BACKEND_URL;

// Slugify display name → /stay/:slug (matches toFeaturedSlug in FeaturedStay.js)
const toSlug = (name) => {
  if (!name) return '';
  return name.split(',')[0].trim().toLowerCase()
    .replace(/['']/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// Partner-tier stays live at /collective/:partnerSlug — use a known map
const PARTNER_SLUGS = {
  'bluehouse by the river': 'bluehouse',
  "eagle's nest": 'eaglenest',
  'tra boutique stay': 'tra',
  'himalayan homestead': 'homestead',
  "raju bharti's cottage": 'rajucottage',
  'brown dipper house': 'browndipper',
  'villa aavaas': 'villaaavaas',
  'srirangam homestay': 'srirangam',
};

const linkFor = (stay) => {
  const name = (stay.display_name || '').toLowerCase().split(',')[0].trim();
  if (stay.partnership_status === 'partner' && PARTNER_SLUGS[name]) {
    return `/collective/${PARTNER_SLUGS[name]}`;
  }
  return `/stay/${toSlug(stay.display_name)}`;
};

const fmtINR = (n) => n ? '₹' + Number(n).toLocaleString('en-IN') : null;

export default function SimilarStays({ currentStay }) {
  const [allStays, setAllStays] = useState([]);
  const { isWishlisted, toggleWishlist, getLikeCount } = useWishlist();

  useEffect(() => {
    if (!currentStay) return;
    fetch(`${API}/api/curated/stays?limit=200`)
      .then((r) => r.json())
      .then((d) => setAllStays(d.stays || []))
      .catch(() => {});
  }, [currentStay?.gohimalayan_id]);

  const matches = useMemo(() => {
    if (!currentStay || !allStays.length) return [];
    const currentTags = new Set((currentStay.experience_types || []).map((t) => t.toLowerCase()));

    const currentId = currentStay.gohimalayan_id;
    const currentName = (currentStay.display_name || '').toLowerCase().trim();
    const currentValley = (currentStay.valley || '').toLowerCase();
    const currentState = (currentStay.state || '').toLowerCase();

    // Pool of candidates: anything active, not the current stay, has at least one image
    const pool = allStays.filter((s) => s.gohimalayan_id !== currentId
                 && (s.display_name || '').toLowerCase().trim() !== currentName
                 && (s.images || []).length > 0);

    // Score each candidate. Multi-signal so we always have a ranking even when
    // experience_types overlap is zero (the previous version returned 0 cards).
    const scored = pool.map((s) => {
      const tags = new Set((s.experience_types || []).map((t) => t.toLowerCase()));
      let score = 0;
      currentTags.forEach((t) => { if (tags.has(t)) score += 3; });
      if (currentValley && (s.valley || '').toLowerCase() === currentValley) score += 2;
      if (currentState && (s.state || '').toLowerCase() === currentState) score += 1;
      // Slight bump for higher tiers so they tend to surface
      if (s.partnership_status === 'featured') score += 0.5;
      else if (s.partnership_status === 'valley_partner') score += 0.4;
      else if (s.partnership_status === 'partner') score += 0.3;
      return { ...s, _score: score };
    });

    return scored
      .sort((a, b) => {
        if (b._score !== a._score) return b._score - a._score;
        const rank = (st) => st.partnership_status === 'featured' ? 0 : st.partnership_status === 'partner' ? 1 : 2;
        return rank(a) - rank(b);
      })
      .slice(0, 4);
  }, [allStays, currentStay]);

  if (matches.length === 0) return null;

  return (
    <section
      className="bg-stone-50 border-t border-stone-200 py-16 md:py-20 px-6 md:px-12"
      data-testid="similar-stays-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="mb-10 md:mb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-900/60 mb-3">
            Hand-picked for you
          </div>
          <h2
            className="text-3xl md:text-4xl text-stone-900 font-light italic"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Stays you might also love
          </h2>
        </div>

        {/* Mobile: horizontal swipe carousel — Desktop: 4-card grid */}
        <div className="flex md:grid md:grid-cols-4 gap-5 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 md:mx-0 px-6 md:px-0 pb-3 md:pb-0 scrollbar-hide">
          {matches.map((stay) => {
            const hero = stay.images?.[0]?.url;
            return (
              <Link
                key={stay.gohimalayan_id}
                to={linkFor(stay)}
                className="group flex-shrink-0 w-[78%] md:w-auto snap-start"
                data-testid={`similar-stay-${stay.gohimalayan_id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-stone-200 mb-4">
                  {hero ? (
                    <img
                      src={hero}
                      alt={stay.display_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  ) : null}
                  {/* Brand badges — identical to /discovered card system */}
                  {stay.partnership_status === 'featured' && (
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                        <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                        </svg>
                        <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Featured</span>
                      </div>
                    </div>
                  )}
                  {stay.partnership_status === 'partner' && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 pointer-events-none" style={{opacity: 0.85}}>
                      <div className="relative flex items-center bg-[#3a5a40]/80 backdrop-blur-sm px-3 py-1.5 rounded-md">
                        <span
                          className="absolute right-2 font-bold text-white/15 leading-none select-none"
                          style={{fontSize: '36px', letterSpacing: '-2px'}}
                        >100</span>
                        <svg className="w-4 h-4 text-white/90 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                        </svg>
                        <div className="relative z-10 flex flex-col leading-tight">
                          <span className="text-[10px] font-semibold text-white tracking-wide" style={{fontFamily: 'Georgia, serif'}}>GoHimalayan</span>
                          <span className="text-[7px] font-medium text-white/70 tracking-[0.2em] uppercase">Collective</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {stay.partnership_status === 'valley_partner' && (
                    <div className="absolute top-3 left-3 pointer-events-none" style={{opacity: 0.9}}>
                      <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm border border-stone-100">
                        <svg className="w-3.5 h-3.5 text-[#3a5a40] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                        </svg>
                        <span className="text-[9px] font-semibold text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Valley Partner</span>
                      </div>
                    </div>
                  )}
                  {!['featured', 'partner', 'valley_partner'].includes(stay.partnership_status) && (
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                        <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                        </svg>
                        <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Discovered</span>
                      </div>
                    </div>
                  )}

                  {/* Wishlist heart + seeded like count — identical to /discovered cards */}
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(stay.gohimalayan_id); }}
                    className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full hover:bg-black/50 transition-colors"
                    data-testid={`similar-wishlist-${stay.gohimalayan_id}`}
                  >
                    <Heart className={`w-3.5 h-3.5 transition-colors ${isWishlisted(stay.gohimalayan_id) ? 'fill-red-500 text-red-500' : 'text-white/80'}`} />
                    <span className="text-[10px] text-white/80 font-medium">{getLikeCount(stay.gohimalayan_id)}</span>
                  </button>
                </div>
                <div className="px-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-1.5">
                    {stay.valley || stay.region}
                  </div>
                  <h3
                    className="text-lg md:text-xl text-stone-900 font-light italic leading-snug mb-2 group-hover:text-amber-900/80 transition-colors"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {(stay.display_name || '').replace(/,.*$/, '').trim()}
                  </h3>
                  {stay.starting_price_inr ? (
                    <div className="text-sm text-stone-600">
                      from <span className="text-stone-900 font-medium">{fmtINR(stay.starting_price_inr)}</span>
                      <span className="text-stone-400"> / night</span>
                    </div>
                  ) : (
                    <div className="text-xs text-stone-400 italic">By enquiry</div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
